import { useState, useEffect, useCallback } from "react";
import { LoginPage } from "./LoginPage";
import {
  Plus,
  Check,
  Pencil,
  Trash2,
  Archive,
  ArchiveRestore,
  X,
  LogOut,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  KeyRound,
  BarChart3,
  Trophy,
} from "lucide-react";
import { ReportsPage } from "./ReportsPage";
import "./index.css";

interface Habit {
  id: number;
  name: string;
  goal_per_week: number;
  archived: number;
  created_at: string;
}

interface Completion {
  habit_id: number;
  completed_date: string;
}

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getWeekRange(date: Date): { start: string; end: string } {
  const d = new Date(date);
  const day = d.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + mondayOffset);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: formatDate(monday), end: formatDate(sunday) };
}

function formatDisplayDate(date: Date): string {
  const today = new Date();
  const todayStr = formatDate(today);
  const dateStr = formatDate(date);
  if (dateStr === todayStr) return "Today";
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (dateStr === formatDate(yesterday)) return "Yesterday";
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (dateStr === formatDate(tomorrow)) return "Tomorrow";
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

function HabitHistory({
  habit,
  onClose,
}: {
  habit: Habit;
  onClose: () => void;
}) {
  const [completions, setCompletions] = useState<Set<string>>(new Set());
  const [calendarDate, setCalendarDate] = useState(new Date());

  useEffect(() => {
    fetch(`/api/habits/${habit.id}/history`)
      .then((r) => r.json())
      .then((data: Completion[]) => {
        setCompletions(new Set(data.map((c) => c.completed_date)));
      })
      .catch(() => {});
  }, [habit.id]);

  const year = calendarDate.getFullYear();
  const month = calendarDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPad = (firstDay.getDay() + 6) % 7; // Monday = 0
  const totalDays = lastDay.getDate();

  const weeks: (number | null)[][] = [];
  let currentWeek: (number | null)[] = Array(startPad).fill(null) as null[];

  for (let day = 1; day <= totalDays; day++) {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) currentWeek.push(null);
    weeks.push(currentWeek);
  }

  function changeMonth(offset: number) {
    const d = new Date(calendarDate);
    d.setMonth(d.getMonth() + offset);
    setCalendarDate(d);
  }

  const monthLabel = calendarDate.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const totalCount = completions.size;

  // Compute stats from completions
  const sortedDates = [...completions].sort();
  const today = formatDate(new Date());

  // Current streak
  let currentStreak = 0;
  const checkDate = new Date();
  if (!completions.has(today)) {
    checkDate.setDate(checkDate.getDate() - 1);
  }
  while (completions.has(formatDate(checkDate))) {
    currentStreak++;
    checkDate.setDate(checkDate.getDate() - 1);
  }

  // Best streak
  let bestStreak = 0;
  if (sortedDates.length > 0) {
    let streak = 1;
    for (let i = 1; i < sortedDates.length; i++) {
      const prev = new Date(sortedDates[i - 1] + "T00:00:00");
      const curr = new Date(sortedDates[i] + "T00:00:00");
      const diff = Math.round((curr.getTime() - prev.getTime()) / (1000 * 60 * 60 * 24));
      if (diff === 1) {
        streak++;
      } else {
        bestStreak = Math.max(bestStreak, streak);
        streak = 1;
      }
    }
    bestStreak = Math.max(bestStreak, streak);
  }

  // Weekly average
  let weeklyAvg = 0;
  if (sortedDates.length > 0) {
    const firstDate = new Date(sortedDates[0] + "T00:00:00");
    const now = new Date();
    const weeks = Math.max(1, Math.ceil((now.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24 * 7)));
    weeklyAvg = totalCount / weeks;
  }

  // Goal completion rate: weeks where goal was met / total weeks
  let goalRate = 0;
  if (sortedDates.length > 0) {
    const weekMap = new Map<string, number>();
    for (const d of sortedDates) {
      const date = new Date(d + "T00:00:00");
      const day = date.getDay();
      const mondayOffset = day === 0 ? -6 : 1 - day;
      const monday = new Date(date);
      monday.setDate(date.getDate() + mondayOffset);
      const key = formatDate(monday);
      weekMap.set(key, (weekMap.get(key) || 0) + 1);
    }
    const metWeeks = [...weekMap.values()].filter((c) => c >= habit.goal_per_week).length;
    goalRate = weekMap.size > 0 ? Math.round((metWeeks / weekMap.size) * 100) : 0;
  }

  // Day-of-week breakdown (Mon=0 .. Sun=6)
  const dayOfWeek = new Array(7).fill(0) as number[];
  for (const d of completions) {
    const date = new Date(d + "T00:00:00");
    const dow = (date.getDay() + 6) % 7;
    dayOfWeek[dow]++;
  }
  const maxDow = Math.max(...dayOfWeek, 1);

  // Monthly totals (most recent first)
  const monthlyTotals: { label: string; count: number }[] = [];
  if (sortedDates.length > 0) {
    const monthMap = new Map<string, number>();
    for (const d of sortedDates) {
      const key = d.slice(0, 7); // "YYYY-MM"
      monthMap.set(key, (monthMap.get(key) || 0) + 1);
    }
    const sortedMonths = [...monthMap.entries()].sort((a, b) => b[0].localeCompare(a[0]));
    for (const [key, count] of sortedMonths) {
      const [y, m] = key.split("-");
      const date = new Date(Number(y), Number(m) - 1, 1);
      const label = date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
      monthlyTotals.push({ label, count });
    }
  }
  const maxMonthly = Math.max(...monthlyTotals.map((m) => m.count), 1);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-[#16213e] border border-white/10 rounded-xl w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <h2 className="text-base font-semibold text-white">{habit.name}</h2>
            <p className="text-xs text-white/40">Goal: {habit.goal_per_week}x per week · Since {new Date(habit.created_at + "Z").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white/70 cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-2 px-4 pt-4">
          <div className="bg-white/5 rounded-lg p-2 text-center">
            <div className="text-lg font-bold text-emerald-400">{totalCount}</div>
            <div className="text-[9px] text-white/40">Total</div>
          </div>
          <div className="bg-white/5 rounded-lg p-2 text-center">
            <div className="text-lg font-bold text-emerald-400">{currentStreak}</div>
            <div className="text-[9px] text-white/40">Streak</div>
          </div>
          <div className="bg-white/5 rounded-lg p-2 text-center">
            <div className="text-lg font-bold text-emerald-400">{bestStreak}</div>
            <div className="text-[9px] text-white/40">Best</div>
          </div>
          <div className="bg-white/5 rounded-lg p-2 text-center">
            <div className="text-lg font-bold text-emerald-400">{goalRate}%</div>
            <div className="text-[9px] text-white/40">Goal Rate</div>
          </div>
        </div>

        {/* Weekly average */}
        <div className="px-4 pt-2">
          <p className="text-xs text-white/40 text-center">{weeklyAvg.toFixed(1)}x per week avg</p>
        </div>

        {/* Month Navigation */}
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => changeMonth(-1)}
            className="p-1 text-white/50 hover:text-white transition-colors cursor-pointer"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="text-sm font-medium text-white">{monthLabel}</span>
          <button
            onClick={() => changeMonth(1)}
            className="p-1 text-white/50 hover:text-white transition-colors cursor-pointer"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="px-4">
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-2 mb-1">
            {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
              <div
                key={d}
                className="text-center text-[10px] text-white/30 font-medium py-1"
              >
                {d}
              </div>
            ))}
          </div>

          {/* Weeks */}
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 gap-2">
              {week.map((day, di) => {
                if (day === null) {
                  return <div key={di} className="aspect-square" />;
                }

                const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                const done = completions.has(dateStr);
                const isToday = dateStr === formatDate(new Date());

                return (
                  <div
                    key={di}
                    className={`aspect-square flex items-center justify-center rounded-full text-xs transition-colors ${
                      done
                        ? "bg-emerald-500 text-white font-medium"
                        : isToday
                          ? "bg-white/10 text-white"
                          : "text-white/40"
                    }`}
                  >
                    {day}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Day-of-week breakdown */}
        <div className="px-4 pt-4">
          <h3 className="text-xs font-medium text-white/50 mb-2">By Day of Week</h3>
          <div className="space-y-1.5">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((label, i) => (
              <div key={label} className="flex items-center gap-2">
                <span className="text-[10px] text-white/40 w-6">{label}</span>
                <div className="flex-1 h-3 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500/60 rounded-full transition-all"
                    style={{ width: `${(dayOfWeek[i] / maxDow) * 100}%` }}
                  />
                </div>
                <span className="text-[10px] text-white/30 w-5 text-right">{dayOfWeek[i]}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Monthly totals */}
        {monthlyTotals.length > 0 && (
          <div className="px-4 py-4">
            <h3 className="text-xs font-medium text-white/50 mb-2">By Month</h3>
            <div className="space-y-1.5">
              {monthlyTotals.map(({ label, count }) => (
                <div key={label} className="flex items-center gap-2">
                  <span className="text-[10px] text-white/40 w-12">{label}</span>
                  <div className="flex-1 h-3 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500/60 rounded-full transition-all"
                      style={{ width: `${(count / maxMonthly) * 100}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-white/30 w-5 text-right">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function App() {
  const [authChecked, setAuthChecked] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [habits, setHabits] = useState<Habit[]>([]);
  const [completions, setCompletions] = useState<Completion[]>([]);
  const [weekCompletions, setWeekCompletions] = useState<Completion[]>([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showArchived, setShowArchived] = useState(false);
  const [editingHabit, setEditingHabit] = useState<Habit | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formName, setFormName] = useState("");
  const [formGoal, setFormGoal] = useState(3);
  const [historyHabit, setHistoryHabit] = useState<Habit | null>(null);
  const [showReports, setShowReports] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState(false);

  useEffect(() => {
    fetch("/api/auth/me")
      .then((r) => r.json())
      .then((data) => {
        if (data.userId) setUserId(data.userId);
      })
      .catch(() => {})
      .finally(() => setAuthChecked(true));
  }, []);

  const dateStr = formatDate(selectedDate);
  const weekRange = getWeekRange(selectedDate);

  const loadData = useCallback(() => {
    if (!userId) return;
    fetch(`/api/habits?includeArchived=${showArchived}`)
      .then((r) => r.json())
      .then(setHabits)
      .catch(() => {});
    fetch(`/api/completions?date=${dateStr}`)
      .then((r) => r.json())
      .then(setCompletions)
      .catch(() => {});
    fetch(`/api/completions?start=${weekRange.start}&end=${weekRange.end}`)
      .then((r) => r.json())
      .then(setWeekCompletions)
      .catch(() => {});
  }, [userId, dateStr, weekRange.start, weekRange.end, showArchived]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  async function handleToggle(habitId: number) {
    await fetch(`/api/habits/${habitId}/complete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ date: dateStr }),
    });
    loadData();
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!formName.trim()) return;
    await fetch("/api/habits", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: formName.trim(), goal_per_week: formGoal }),
    });
    setFormName("");
    setFormGoal(3);
    setShowAddForm(false);
    loadData();
  }

  async function handleUpdate(e: React.FormEvent) {
    e.preventDefault();
    if (!editingHabit || !formName.trim()) return;
    await fetch(`/api/habits/${editingHabit.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: formName.trim(), goal_per_week: formGoal }),
    });
    setEditingHabit(null);
    setFormName("");
    setFormGoal(3);
    loadData();
  }

  async function handleDelete(id: number) {
    if (!confirm("Delete this habit and all its history?")) return;
    await fetch(`/api/habits/${id}`, { method: "DELETE" });
    loadData();
  }

  async function handleArchive(id: number, archive: boolean) {
    await fetch(`/api/habits/${id}/archive`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ archived: archive }),
    });
    loadData();
  }

  async function handleLogout() {
    await fetch("/api/auth/logout", { method: "POST" });
    setUserId(null);
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwError("");
    setPwSuccess(false);
    if (pwNew !== pwConfirm) {
      setPwError("Passwords do not match");
      return;
    }
    const res = await fetch("/api/auth/change-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ newPassword: pwNew }),
    });
    const data = await res.json();
    if (!res.ok) {
      setPwError(data.error);
      return;
    }
    setPwSuccess(true);
    setPwNew("");
    setPwConfirm("");
  }

  function startEdit(habit: Habit) {
    setEditingHabit(habit);
    setFormName(habit.name);
    setFormGoal(habit.goal_per_week);
    setShowAddForm(false);
  }

  function startAdd() {
    setShowAddForm(true);
    setEditingHabit(null);
    setFormName("");
    setFormGoal(3);
  }

  function cancelForm() {
    setShowAddForm(false);
    setEditingHabit(null);
    setFormName("");
    setFormGoal(3);
  }

  function changeDate(offset: number) {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + offset);
    setSelectedDate(d);
  }

  function isCompleted(habitId: number): boolean {
    return completions.some((c) => c.habit_id === habitId);
  }

  function weekCount(habitId: number): number {
    return weekCompletions.filter((c) => c.habit_id === habitId).length;
  }

  if (!authChecked) return null;
  if (!userId) return <LoginPage onLogin={setUserId} />;

  const activeHabits = habits
    .filter((h) => !h.archived)
    .slice()
    .sort((a, b) => Number(isCompleted(a.id)) - Number(isCompleted(b.id)));
  const archivedHabits = habits.filter((h) => h.archived);

  return (
    <div className="min-h-screen bg-[#1a1a2e]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#16213e]">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <h1 className="text-lg font-bold text-white">Habits</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowReports(!showReports)}
              className={`transition-colors cursor-pointer ${showReports ? "text-emerald-400" : "text-white/40 hover:text-white/70"}`}
              title="Reports"
            >
              <BarChart3 size={18} />
            </button>
            <button
              onClick={() => {
                setShowPasswordForm(!showPasswordForm);
                setPwError("");
                setPwSuccess(false);
                setPwNew("");
                setPwConfirm("");
              }}
              className="text-white/40 hover:text-white/70 transition-colors cursor-pointer"
              title="Change password"
            >
              <KeyRound size={18} />
            </button>
            <button
              onClick={handleLogout}
              className="text-white/40 hover:text-white/70 transition-colors cursor-pointer"
              title="Sign out"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      {showReports ? (
        <ReportsPage onBack={() => setShowReports(false)} />
      ) : (
      <>
      {showPasswordForm && (
        <div className="max-w-2xl mx-auto px-4 pt-4">
          <form
            onSubmit={handleChangePassword}
            className="p-4 rounded-lg bg-[#16213e] border border-white/10"
          >
            <h3 className="text-sm font-medium text-white mb-3">Change Password</h3>
            <div className="space-y-3">
              <input
                type="password"
                value={pwNew}
                onChange={(e) => setPwNew(e.target.value)}
                placeholder="New password"
                autoComplete="new-password"
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-sm text-white placeholder-white/20 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                required
              />
              <input
                type="password"
                value={pwConfirm}
                onChange={(e) => setPwConfirm(e.target.value)}
                placeholder="Confirm new password"
                autoComplete="new-password"
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-sm text-white placeholder-white/20 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                required
              />
              {pwError && (
                <p className="text-xs text-red-400">{pwError}</p>
              )}
              {pwSuccess && (
                <p className="text-xs text-emerald-400">Password changed successfully</p>
              )}
              <button
                type="submit"
                className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded transition-colors cursor-pointer"
              >
                Change Password
              </button>
            </div>
          </form>
        </div>
      )}

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Date Navigation */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => changeDate(-1)}
            className="p-2 text-white/50 hover:text-white transition-colors cursor-pointer"
          >
            <ChevronLeft size={20} />
          </button>
          <div className="text-center">
            <div className="text-lg font-semibold text-white">
              {formatDisplayDate(selectedDate)}
            </div>
            <div className="text-xs text-white/40">
              {selectedDate.toLocaleDateString("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </div>
          </div>
          <button
            onClick={() => changeDate(1)}
            className="p-2 text-white/50 hover:text-white transition-colors cursor-pointer"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Habit List */}
        <div className="space-y-2 mb-6">
          {activeHabits.length === 0 && !showAddForm && (
            <div className="text-center py-12 text-white/30">
              <p className="mb-2">No habits yet</p>
              <button
                onClick={startAdd}
                className="text-emerald-400 hover:text-emerald-300 text-sm cursor-pointer"
              >
                Add your first habit
              </button>
            </div>
          )}

          {activeHabits.map((habit) => {
            const completed = isCompleted(habit.id);
            const wc = weekCount(habit.id);
            const goalMet = wc >= habit.goal_per_week;

            return (
              <div
                key={habit.id}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                  completed
                    ? "bg-emerald-500/10 border-emerald-500/30"
                    : goalMet
                      ? "bg-amber-500/10 border-amber-500/30"
                      : "bg-white/5 border-white/10 hover:border-white/20"
                }`}
              >
                {/* Checkbox */}
                <button
                  onClick={() => handleToggle(habit.id)}
                  className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
                    completed
                      ? "bg-emerald-500 border-emerald-500 text-white"
                      : "border-white/30 hover:border-emerald-400"
                  }`}
                >
                  {completed && <Check size={14} strokeWidth={3} />}
                </button>

                {/* Name and progress */}
                <div className="flex-1 min-w-0">
                  <button
                    onClick={() => setHistoryHabit(habit)}
                    className={`text-sm font-medium text-left cursor-pointer hover:text-emerald-300 transition-colors flex items-center gap-1.5 ${
                      completed ? "text-white/50 line-through" : "text-white"
                    }`}
                  >
                    <span>{habit.name}</span>
                    {goalMet && (
                      <Trophy
                        size={13}
                        className="text-amber-400 flex-shrink-0"
                      />
                    )}
                  </button>
                  <div className="text-xs text-white/40">
                    <span className={goalMet ? "text-amber-400" : ""}>
                      {wc}/{habit.goal_per_week}
                    </span>{" "}
                    this week
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setHistoryHabit(habit)}
                    className="p-1.5 text-white/20 hover:text-white/60 transition-colors cursor-pointer"
                    title="History"
                  >
                    <CalendarDays size={14} />
                  </button>
                  <button
                    onClick={() => startEdit(habit)}
                    className="p-1.5 text-white/20 hover:text-white/60 transition-colors cursor-pointer"
                    title="Edit"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => handleArchive(habit.id, true)}
                    className="p-1.5 text-white/20 hover:text-white/60 transition-colors cursor-pointer"
                    title="Archive"
                  >
                    <Archive size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(habit.id)}
                    className="p-1.5 text-white/20 hover:text-red-400 transition-colors cursor-pointer"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add / Edit Form */}
        {(showAddForm || editingHabit) && (
          <form
            onSubmit={editingHabit ? handleUpdate : handleAdd}
            className="mb-6 p-4 rounded-lg bg-white/5 border border-white/10"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-white">
                {editingHabit ? "Edit Habit" : "New Habit"}
              </h3>
              <button
                type="button"
                onClick={cancelForm}
                className="text-white/40 hover:text-white/70 cursor-pointer"
              >
                <X size={16} />
              </button>
            </div>
            <div className="space-y-3">
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Habit name"
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-sm text-white placeholder-white/20 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                required
                autoFocus
              />
              <div className="flex items-center gap-3">
                <label className="text-xs text-white/60 whitespace-nowrap">
                  Goal: {formGoal}x per week
                </label>
                <input
                  type="range"
                  min={1}
                  max={7}
                  value={formGoal}
                  onChange={(e) => setFormGoal(Number(e.target.value))}
                  className="flex-1 accent-emerald-500"
                />
              </div>
              <button
                type="submit"
                className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded transition-colors cursor-pointer"
              >
                {editingHabit ? "Save Changes" : "Add Habit"}
              </button>
            </div>
          </form>
        )}

        {/* Add Button */}
        {!showAddForm && !editingHabit && activeHabits.length > 0 && (
          <button
            onClick={startAdd}
            className="w-full py-2.5 border border-dashed border-white/20 hover:border-emerald-500/50 rounded-lg text-sm text-white/40 hover:text-emerald-400 transition-colors flex items-center justify-center gap-2 cursor-pointer"
          >
            <Plus size={16} />
            Add Habit
          </button>
        )}

        {/* Archived Section */}
        <div className="mt-8">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className="text-xs text-white/30 hover:text-white/50 transition-colors cursor-pointer"
          >
            {showArchived ? "Hide archived" : "Show archived"}
          </button>

          {showArchived && archivedHabits.length > 0 && (
            <div className="mt-3 space-y-2">
              {archivedHabits.map((habit) => (
                <div
                  key={habit.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/5"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white/30">{habit.name}</div>
                    <div className="text-xs text-white/20">
                      {habit.goal_per_week}x/week - Archived
                    </div>
                  </div>
                  <button
                    onClick={() => handleArchive(habit.id, false)}
                    className="p-1.5 text-white/20 hover:text-emerald-400 transition-colors cursor-pointer"
                    title="Restore"
                  >
                    <ArchiveRestore size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(habit.id)}
                    className="p-1.5 text-white/20 hover:text-red-400 transition-colors cursor-pointer"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {showArchived && archivedHabits.length === 0 && (
            <p className="mt-2 text-xs text-white/20">No archived habits</p>
          )}
        </div>
      </main>

      {historyHabit && (
        <HabitHistory
          habit={historyHabit}
          onClose={() => setHistoryHabit(null)}
        />
      )}
      </>
      )}
    </div>
  );
}

export default App;
